// Serializes async tasks per key. Operations queued under the same key run
// strictly in submission order; different keys run independently. Used by
// the progress writer so two flushes for the same (profile, dateKey) cannot
// interleave their read-modify-write cycle and clobber each other.

const inFlight = new Map<string, Promise<unknown>>();

export function runOnKey<T>(key: string, task: () => Promise<T>): Promise<T> {
  const previous = inFlight.get(key) ?? Promise.resolve();
  const next = previous.catch(() => undefined).then(() => task());
  inFlight.set(key, next);
  // Clean up once this task settles, but only if no newer task chained on.
  void next
    .catch(() => undefined)
    .finally(() => {
      if (inFlight.get(key) === next) inFlight.delete(key);
    });
  return next;
}

// For tests: drop all queued state.
export function resetQueueForTests(): void {
  inFlight.clear();
}

// For tests: await every currently-queued chain. Useful in afterEach to
// guarantee a previous test's debounced IDB writes have settled before the
// next test clears IDB — otherwise an in-flight save can land *after*
// clearIdb() and pollute the next test's hydration.
export async function drainQueueForTests(): Promise<void> {
  const pending = Array.from(inFlight.values());
  await Promise.allSettled(pending);
}
